/**
 * Drift Monitoring Settings Page
 * Allows administrators to configure drift detection parameters
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  Text,
  Switch,
  Slider,
  Button,
  makeStyles,
  tokens,
  Spinner,
  Dropdown,
  Option,
  Badge,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../components/Common/ScrollableMessageBar';
import {
  Settings24Regular,
  ArrowSync24Regular,
  Save24Regular,
  Checkmark24Regular,
  Warning24Regular,
  Info24Regular,
  ArrowLeftRegular } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXL,
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: tokens.spacingHorizontalL
  },
  card: {
    marginBottom: tokens.spacingVerticalL
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalM} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  settingInfo: {
    flex: 1
  },
  settingControl: {
    minWidth: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  sliderContainer: {
    width: '200px'
  },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS
  },
  statusCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalL
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL
  }
});

interface MonitoringConfig {
  monitoringEnabled: boolean;
  checkIntervalMinutes: number;
  deduplicationEnabled: boolean;
  autoCleanupDays: number;
  notificationChannels: string[];
  bypassNotifications: boolean;
}

interface QueueStats {
  pending: number;
  notified: number;
  resolved: number;
  added: number;
  deleted: number;
  modified: number;
  lastDetection: string | null;
}

export const DriftMonitoringSettings: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<MonitoringConfig>({
    monitoringEnabled: true,
    checkIntervalMinutes: 5,
    deduplicationEnabled: true,
    autoCleanupDays: 30,
    notificationChannels: ['ui', 'websocket'],
    bypassNotifications: false
  });
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error';text: string;} | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfiguration();
    loadQueueStats();

    // Refresh stats every 30 seconds
    const interval = setInterval(loadQueueStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/config/drift_monitoring');
      setConfig(response.data as MonitoringConfig);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
      setConfig({
        monitoringEnabled: true,
        checkIntervalMinutes: 5,
        deduplicationEnabled: true,
        autoCleanupDays: 30,
        notificationChannels: ['ui', 'websocket'],
        bypassNotifications: false
      });
    } finally {
      setLoading(false);
    }
  };

  const loadQueueStats = async () => {
    try {
      // Use drift data to simulate queue stats
      const response = await apiClient.get('/policies/drift');
      const data = (response?.data || response) as {success: boolean;stats: QueueStats;};
      if (data?.success) {
        setQueueStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load queue stats:', error);
    }
  };

  const handleConfigChange = (key: keyof MonitoringConfig, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put('/config/drift_monitoring', config);
      if (response?.success) {
        setMessage({ type: 'success', text: 'Configuration saved successfully' });
        setHasChanges(false);

        // If monitoring was just enabled, trigger immediate check
        if (config.monitoringEnabled) {
          await triggerManualCheck();
        }
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const triggerManualCheck = async () => {
    try {
      setMessage({ type: 'success', text: 'Triggering drift check...' });
      const response = await apiClient.get('/policies/drift');
      const data = (response?.data || response) as {success: boolean;dbCount: number;teamsCount: number;};
      if (response?.success) {
        setMessage({
          type: 'success',
          text: `Drift check complete: ${Math.abs(data.teamsCount - data.dbCount)}/${data.teamsCount} policies with drift`
        });
        await loadQueueStats();
      }
    } catch (error) {
      console.error('Failed to trigger drift check:', error);
      setMessage({ type: 'error', text: 'Failed to trigger drift check' });
    }
  };

  const clearQueue = async () => {
    if (!window.confirm('Are you sure you want to clear all resolved drift entries?')) {
      return;
    }

    try {
      // Queue cleanup not implemented yet - simulate success
      const response = { success: true, deletedCount: 0 };
      const data = response as {success: boolean;deletedCount: number;};
      if (data?.success) {
        setMessage({
          type: 'success',
          text: `Cleared ${data.deletedCount} resolved entries`
        });
        await loadQueueStats();
      }
    } catch (error) {
      console.error('Failed to clear queue:', error);
      setMessage({ type: 'error', text: 'Failed to clear queue' });
    }
  };

  if (loading) {
    return (
      <div className={styles.container} data-testid="drift-monitoring-settings-container-div">
        <Spinner size="large" label="Loading drift monitoring settings..." data-testid="drift-monitoring-settings-loading-drift-monitoring-settings-spinner" />
      </div>);

  }

  return (
    <div className={styles.container} data-testid="drift-monitoring-settings-container-2-div">
      <div className={styles.header} data-testid="drift-monitoring-settings-header-div">
        <div data-testid="drift-monitoring-settings-header-content-div">
          <div className={styles.title} data-testid="drift-monitoring-settings-title-div">
            <Settings24Regular data-testid="drift-monitoring-settings-settings24-regular" />
            <Text size={600} weight="semibold" data-testid="drift-monitoring-settings-drift-monitoring-settings-text">Drift Monitoring Settings</Text>
          </div>
          <Text size={300} data-testid="drift-monitoring-settings-configure-how-the-system-monitors-and-text">
            Configure how the system monitors and detects policy drift between Teams and ADM
          </Text>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular data-testid="drift-monitoring-settings-arrow-left-regular" />}
          onClick={() => navigate('/settings')} data-testid="drift-monitoring-settings-back-to-settings-button">

          Back to Settings
        </Button>
      </div>

      {message &&
      <MessageBar intent={message.type === 'error' ? 'error' : 'success'} data-testid="drift-monitoring-settings-message-bar">
          <MessageBarBody data-testid="drift-monitoring-settings-message-bar-body">{message.text}</MessageBarBody>
        </MessageBar>
      }

      {/* Status Card */}
      <Card className={styles.statusCard} data-testid="drift-monitoring-settings-status-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} data-testid="drift-monitoring-settings-monitor-status-header-div">
          <div data-testid="drift-monitoring-settings-monitor-status-summary-div">
            <Text size={400} weight="semibold" data-testid="drift-monitoring-settings-monitor-status-text">Monitor Status</Text>
            <div style={{ marginTop: '8px' }} data-testid="drift-monitoring-settings-monitor-status-badge-div">
              {config.monitoringEnabled ?
              <Badge appearance="filled" color="success" data-testid="drift-monitoring-settings-active-badge">
                  <Checkmark24Regular style={{ fontSize: '12px' }} data-testid="drift-monitoring-settings-checkmark24-regular" /> Active
                </Badge> :

              <Badge appearance="filled" color="warning" data-testid="drift-monitoring-settings-inactive-badge">
                  <Warning24Regular style={{ fontSize: '12px' }} data-testid="drift-monitoring-settings-warning24-regular" /> Inactive
                </Badge>
              }
            </div>
          </div>
          <Button
            icon={<ArrowSync24Regular data-testid="drift-monitoring-settings-arrow-sync24-regular" />}
            onClick={triggerManualCheck}
            disabled={!config.monitoringEnabled} data-testid="drift-monitoring-settings-check-now-button">

            Check Now
          </Button>
        </div>
        
        {queueStats &&
        <div className={styles.statusGrid} data-testid="drift-monitoring-settings-status-grid-div">
            <div className={styles.statusItem} data-testid="drift-monitoring-settings-status-item-div">
              <Text size={500} weight="semibold" data-testid="drift-monitoring-settings-pending-value-text">{queueStats.pending}</Text>
              <Text size={200} data-testid="drift-monitoring-settings-pending-text">Pending</Text>
            </div>
            <div className={styles.statusItem} data-testid="drift-monitoring-settings-status-item-2-div">
              <Text size={500} weight="semibold" data-testid="drift-monitoring-settings-added-value-text">{queueStats.added}</Text>
              <Text size={200} data-testid="drift-monitoring-settings-added-text">Added</Text>
            </div>
            <div className={styles.statusItem} data-testid="drift-monitoring-settings-status-item-3-div">
              <Text size={500} weight="semibold" data-testid="drift-monitoring-settings-deleted-value-text">{queueStats.deleted}</Text>
              <Text size={200} data-testid="drift-monitoring-settings-deleted-text">Deleted</Text>
            </div>
            <div className={styles.statusItem} data-testid="drift-monitoring-settings-status-item-4-div">
              <Text size={500} weight="semibold" data-testid="drift-monitoring-settings-modified-value-text">{queueStats.modified}</Text>
              <Text size={200} data-testid="drift-monitoring-settings-modified-text">Modified</Text>
            </div>
            <div className={styles.statusItem} data-testid="drift-monitoring-settings-status-item-5-div">
              <Text size={500} weight="semibold" data-testid="drift-monitoring-settings-resolved-value-text">{queueStats.resolved}</Text>
              <Text size={200} data-testid="drift-monitoring-settings-resolved-text">Resolved</Text>
            </div>
          </div>
        }
        
        {queueStats?.lastDetection &&
        <div style={{ marginTop: '12px' }} data-testid="drift-monitoring-settings-last-detection-div">
            <Text size={200} data-testid="drift-monitoring-settings-last-detection-text">
              Last detection: {new Date(queueStats.lastDetection).toLocaleString()}
            </Text>
          </div>
        }
      </Card>

      <div className={styles.grid} data-testid="drift-monitoring-settings-grid-div">
        {/* Core Settings */}
        <Card className={styles.card} data-testid="drift-monitoring-settings-core-settings-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="drift-monitoring-settings-core-settings-text">Core Settings</Text>}
            description="Configure the main drift detection behavior" data-testid="drift-monitoring-settings-core-settings-card-header" />

          
          <div className={styles.settingRow} data-testid="drift-monitoring-settings-setting-row-div">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-enable-monitoring-text">Enable Monitoring</Text>
              <Text size={200} data-testid="drift-monitoring-settings-turn-on-background-drift-detection-for-text">
                Turn on background drift detection for all policies
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-div">
              <Switch
                checked={config.monitoringEnabled}
                onChange={(_, data) => handleConfigChange('monitoringEnabled', data.checked)} data-testid="drift-monitoring-settings-monitoring-enabled-switch" />

            </div>
          </div>

          <div className={styles.settingRow} data-testid="drift-monitoring-settings-bypass-notifications-row">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-2-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-bypass-drift-notifications-text">Bypass Drift Notifications</Text>
              <Text size={200} data-testid="drift-monitoring-settings-automatically-sync-changes-without-manual-approval-text">
                Automatically sync changes without manual approval (manual approval requires clicking Accept/Dismiss for each drift)
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-2-div">
              <Switch
                checked={config.bypassNotifications}
                onChange={(_, data) => handleConfigChange('bypassNotifications', data.checked)}
                disabled={!config.monitoringEnabled} data-testid="drift-monitoring-settings-bypass-notifications-switch" />

            </div>
          </div>

          <div className={styles.settingRow} data-testid="drift-monitoring-settings-check-interval-row">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-3-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-check-interval-text">Check Interval</Text>
              <Text size={200} data-testid="drift-monitoring-settings-how-often-to-check-for-drift-text">
                How often to check for drift (in minutes)
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-3-div">
              <div className={styles.sliderContainer} data-testid="drift-monitoring-settings-slider-container-div">
                <div className={styles.sliderLabel} data-testid="drift-monitoring-settings-slider-label-div">
                  <Text size={200} data-testid="drift-monitoring-settings-1-min-text">1 min</Text>
                  <Text size={300} weight="semibold" data-testid="drift-monitoring-settings-min-text">{config.checkIntervalMinutes} min</Text>
                  <Text size={200} data-testid="drift-monitoring-settings-60-min-text">60 min</Text>
                </div>
                <Slider
                  min={1}
                  max={60}
                  step={1}
                  value={config.checkIntervalMinutes}
                  onChange={(_, data) => handleConfigChange('checkIntervalMinutes', data.value)}
                  disabled={!config.monitoringEnabled} data-testid="drift-monitoring-settings-check-interval-slider" />

              </div>
            </div>
          </div>

          <div className={styles.settingRow} data-testid="drift-monitoring-settings-deduplication-row">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-4-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-enable-deduplication-text">Enable Deduplication</Text>
              <Text size={200} data-testid="drift-monitoring-settings-prevent-duplicate-notifications-for-the-same-text">
                Prevent duplicate notifications for the same drift
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-4-div">
              <Switch
                checked={config.deduplicationEnabled}
                onChange={(_, data) => handleConfigChange('deduplicationEnabled', data.checked)} data-testid="drift-monitoring-settings-deduplication-switch" />

            </div>
          </div>
        </Card>

        {/* Notification Settings */}
        <Card className={styles.card} data-testid="drift-monitoring-settings-notification-settings-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="drift-monitoring-settings-notification-settings-text">Notification Settings</Text>}
            description="Configure how drift notifications are delivered" data-testid="drift-monitoring-settings-notification-settings-card-header" />

          
          <div className={styles.settingRow} data-testid="drift-monitoring-settings-notification-channels-row">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-5-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-notification-channels-text">Notification Channels</Text>
              <Text size={200} data-testid="drift-monitoring-settings-select-how-to-receive-drift-notifications-text">
                Select how to receive drift notifications
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-5-div">
              <Dropdown
                multiselect
                placeholder="Select channels"
                value={config.notificationChannels.join(', ')}
                selectedOptions={config.notificationChannels}
                onOptionSelect={(_, data) => {
                  const selected = data.selectedOptions as string[];
                  handleConfigChange('notificationChannels', selected);
                }} data-testid="drift-monitoring-settings-select-channels-dropdown">

                <Option value="ui" data-testid="drift-monitoring-settings-ui-banner-option">UI Banner</Option>
                <Option value="websocket" data-testid="drift-monitoring-settings-real-time-web-socket-option">Real-time (WebSocket)</Option>
                <Option value="email" disabled data-testid="drift-monitoring-settings-email-coming-soon-option">Email (Coming Soon)</Option>
                <Option value="webhook" disabled data-testid="drift-monitoring-settings-webhook-coming-soon-option">Webhook (Coming Soon)</Option>
              </Dropdown>
            </div>
          </div>

          <div className={styles.settingRow} data-testid="drift-monitoring-settings-auto-cleanup-row">
            <div className={styles.settingInfo} data-testid="drift-monitoring-settings-setting-info-6-div">
              <Text weight="semibold" data-testid="drift-monitoring-settings-auto-cleanup-period-text">Auto-cleanup Period</Text>
              <Text size={200} data-testid="drift-monitoring-settings-days-to-keep-resolved-drift-entries-text">
                Days to keep resolved drift entries
              </Text>
            </div>
            <div className={styles.settingControl} data-testid="drift-monitoring-settings-setting-control-6-div">
              <div className={styles.sliderContainer} data-testid="drift-monitoring-settings-slider-container-2-div">
                <div className={styles.sliderLabel} data-testid="drift-monitoring-settings-slider-label-2-div">
                  <Text size={200} data-testid="drift-monitoring-settings-7-days-text">7 days</Text>
                  <Text size={300} weight="semibold" data-testid="drift-monitoring-settings-days-text">{config.autoCleanupDays} days</Text>
                  <Text size={200} data-testid="drift-monitoring-settings-90-days-text">90 days</Text>
                </div>
                <Slider
                  min={7}
                  max={90}
                  step={1}
                  value={config.autoCleanupDays}
                  onChange={(_, data) => handleConfigChange('autoCleanupDays', data.value)} data-testid="drift-monitoring-settings-auto-cleanup-slider" />

              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Info Box */}
      <Card style={{ marginBottom: tokens.spacingVerticalL }} data-testid="drift-monitoring-settings-info-card">
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-start' }} data-testid="drift-monitoring-settings-info-box-layout-div">
          <Info24Regular style={{ color: tokens.colorBrandForeground1 }} data-testid="drift-monitoring-settings-info24-regular" />
          <div data-testid="drift-monitoring-settings-info-box-content-div">
            <Text weight="semibold" data-testid="drift-monitoring-settings-how-drift-detection-works-text">How Drift Detection Works</Text>
            <Text size={200} style={{ display: 'block', marginTop: '4px' }} data-testid="drift-monitoring-settings-the-system-continuously-monitors-teams-policies-text">
              The system continuously monitors Teams policies and compares them with ADM records:
            </Text>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }} data-testid="drift-monitoring-settings-detection-rules-list-ul">
              <li data-testid="drift-monitoring-settings-detection-rule-new-policies-li"><Text size={200} data-testid="drift-monitoring-settings-checks-for-new-policies-added-in-text">Checks for new policies added in Teams but not in ADM</Text></li>
              <li data-testid="drift-monitoring-settings-detection-rule-deleted-policies-li"><Text size={200} data-testid="drift-monitoring-settings-detects-policies-deleted-from-teams-but-text">Detects policies deleted from Teams but still in ADM</Text></li>
              <li data-testid="drift-monitoring-settings-detection-rule-policy-changes-li"><Text size={200} data-testid="drift-monitoring-settings-identifies-changes-in-domains-groups-ranks-text">Identifies changes in domains, groups, ranks, and settings</Text></li>
              <li data-testid="drift-monitoring-settings-detection-rule-deduplication-li"><Text size={200} data-testid="drift-monitoring-settings-uses-sha256-hashing-to-detect-changes-text">Uses SHA256 hashing to detect changes and prevent duplicates</Text></li>
              <li data-testid="drift-monitoring-settings-detection-rule-review-queue-li"><Text size={200} data-testid="drift-monitoring-settings-queues-notifications-for-administrator-review-and-text">Queues notifications for administrator review and approval</Text></li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className={styles.actions} data-testid="drift-monitoring-settings-actions-div">
        <Button
          appearance="primary"
          icon={<Save24Regular data-testid="drift-monitoring-settings-save24-regular" />}
          onClick={handleSave}
          disabled={!hasChanges || saving} data-testid="drift-monitoring-settings-button">

          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          appearance="secondary"
          onClick={clearQueue}
          disabled={!queueStats || queueStats.resolved === 0} data-testid="drift-monitoring-settings-clear-resolved-button">

          Clear Resolved ({queueStats?.resolved || 0})
        </Button>
        <Button
          appearance="subtle"
          onClick={loadConfiguration}
          disabled={loading} data-testid="drift-monitoring-settings-reset-button">

          Reset
        </Button>
      </div>
    </div>);

};

export default DriftMonitoringSettings;